/* eslint-disable no-nested-ternary */
import React, { forwardRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { InputAdornment } from '@mui/material';
import { NumericFormat } from 'react-number-format';
import ReadOnly from './ReadOnly';
import Currency from '../../../product-framework/components/Currency';
import TextField from './TextField';
import { useEntityPermission } from '../../providers/EntityPermissionProvider/index';

const NumericFormatCustom = React.forwardRef(({ onChange, ...props }, ref) => (
  <NumericFormat
    {...props}
    getInputRef={ref}
    valueIsNumericString
    decimalScale={2}
    fixedDecimalScale
    onValueChange={values => {
      onChange({
        target: {
          name: props.name,
          value: Number(values.value),
        },
      });
    }}
  />
));

const NumericFormatPositiveNumbers = React.forwardRef(
  ({ onChange, decimalScale = 2, ...props }, ref) => (
    <NumericFormat
      {...props}
      getInputRef={ref}
      valueIsNumericString
      decimalScale={decimalScale}
      isAllowed={values => {
        const { floatValue } = values;
        return floatValue === undefined || floatValue >= 0;
      }}
      fixedDecimalScale
      onValueChange={values => {
        onChange({
          target: {
            name: props.name,
            value: Number(values.value),
          },
        });
      }}
    />
  ),
);

/*
 * ReadOnlyValue field is needed in situations where the value is an array of objects[{type, value}]
 * But you want only the value for a particular type
 * So in this case value=[{type,value},{type,value}], but you can send readOnlyValue={value} (whichever type you want)
 */

const NumericTextField = forwardRef(
  (
    {
      name,
      startAdornment = false,
      isCurrency = false,
      positiveNumber,
      overwriteProps,
      readOnlyValue = 'EMPTY',
      decimalScale = 2,
      allowNull = false,
      helperText = null,
      ...props
    },
    ref,
  ) => {
    const { control, getValues } = useFormContext({});

    const { isHidden, isReadOnly } = useEntityPermission();

    const currentvalue =
      readOnlyValue === 'EMPTY' ? getValues(name) : readOnlyValue;

    if (isReadOnly(props?.permission, currentvalue)) {
      return (
        <ReadOnly name={name}>
          {isCurrency ? (
            <Currency>{currentvalue}</Currency>
          ) : (
            <>
              {startAdornment || ''}
              {currentvalue}
            </>
          )}
        </ReadOnly>
      );
    }

    if (isHidden(props?.permission)) {
      return <input type="hidden" name={name} value={currentvalue} />;
    }

    return (
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error: fsError } }) => {
          return (
            <TextField
              {...field}
              value={
                field.value === undefined ? (allowNull ? null : 0) : field.value
              }
              ref={ref}
              error={fsError?.message}
              helperText={helperText}
              InputProps={{
                inputComponent: positiveNumber
                  ? NumericFormatPositiveNumbers
                  : NumericFormatCustom,
                inputProps: {
                  decimalScale,
                },
                ...(startAdornment !== false && {
                  startAdornment: (
                    <InputAdornment position="start">
                      {startAdornment}
                    </InputAdornment>
                  ),
                }),
              }}
              disabled={props?.disabled || false}
              {...overwriteProps}
            />
          );
        }}
      />
    );
  },
);

export default NumericTextField;
